import { useRef, useState } from "react";
import { Helmet } from "react-helmet";
import blueWave from "../../assets/blue_wave.mp4";
import { useIsUserSignedIn } from "../../core_stores/useAuthStore";
import { useSnackbar } from "../../providers/SnackbarProvider";
import {
  ALLOWED_IMAGE_TYPES,
  usePromptInputActions,
} from "../../view_stores/usePromptInputStore";
import {
  SideMenuBehavior,
  useSideMenuBehavior,
} from "../../view_stores/useSideMenuStore";
import ImageDropArea from "../common_components/ImageDropArea";
import SideMenuPanel from "../sideMenu/SideMenuPanel";
import { WelcomeAppBar } from "./components/WelcomeAppBar";
import { WelcomeDescription } from "./components/WelcomeDescription";
import { WelcomeDevicesPreview } from "./components/WelcomeDevicesPreview";
import { WelcomeFooter } from "./components/WelcomeFooter";
import WelcomeFreeToTryInfo from "./components/WelcomeFreeToTryInfo";
import { WelcomeBlogFeed } from "./components/WelcomeBlogFeed";
import { WelcomeTextField } from "./components/WelcomeTextField";

export function WelcomePageDefault() {
  const isUserSignedIn = useIsUserSignedIn();
  const sideMenuBehavior = useSideMenuBehavior();
  const { setAttachedImage } = usePromptInputActions();
  const [isDragging, setIsDragging] = useState(false);
  const [isValidFileType, setIsValidFileType] = useState<boolean>(true);
  const dragCounter = useRef(0);
  const { showSnackbar } = useSnackbar();

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer?.types.includes("Files")) {
      const items = e.dataTransfer?.items;
      if (items?.length === 1) {
        const item = items[0];
        setIsValidFileType(ALLOWED_IMAGE_TYPES.includes(item.type));
      }
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
      setIsValidFileType(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    setIsValidFileType(true);

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setAttachedImage(file);
    } else {
      showSnackbar("Unsupported file type", {
        type: "error",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Darvin - AI-Powered No-Code Flutter Mobile App Builder from Text</title>
      </Helmet>
      <div className="h-[100dvh] min-h-[900px] flex">
        {isUserSignedIn && <SideMenuPanel />}
      <div
        className={`${
          isUserSignedIn &&
          (sideMenuBehavior === SideMenuBehavior.PINNED
            ? "pl-[320px]"
            : "pl-[60px]")
        } flex-1 flex flex-col transition-[padding] duration-300 
        relative`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="flex-1 flex flex-col relative">
          <div className="min-h-[45%] flex flex-col relative">
            <div className="absolute right-0 top-0 w-full h-full overflow-hidden">
              <video
                src={blueWave}
                autoPlay
                loop
                muted
                className="w-full h-full object-cover scale-[1.1]"
              />
            </div>
            <div
              className="absolute inset-0 bg-gradient-to-t 
              from-light-surface-primary dark:from-dark-surface-primary via-[#03142800] 
              to-transparent pointer-events-none"
            />
            {/* <WelcomePromoCode /> */}
            <WelcomeAppBar />
            <div className="flex-1 flex justify-center items-center px-8">
              <div
                className="flex-1 max-w-[1300px] flex flex-row items-center justify-center 
              gap-2 pt-6 pb-10"
              >
                <div className="flex-1 min-w-[45%] flex flex-col items-center justify-center gap-4">
                  <div className="w-full flex flex-col items-center justify-center gap-8">
                    <WelcomeDescription />
                    <WelcomeTextField />
                  </div>
                  <WelcomeFreeToTryInfo />
                </div>
                <div className="h-full flex items-center justify-center">
                  <WelcomeDevicesPreview />
                </div>
              </div>
            </div>
          </div>
          <div
            className="flex-1 flex flex-col items-center justify-center
            bg-light-surface-primary dark:bg-dark-surface-primary px-8"
          >
            <div
              className="w-full max-w-[1300px] flex flex-col
              gap-5 pt-4"
            >
              {/* <WelcomeTryOutIdeas /> */}
              <WelcomeBlogFeed />
            </div>
          </div>
          <WelcomeFooter />
          {isDragging && <ImageDropArea isValidFileType={isValidFileType} />}
        </div>
      </div>
    </div>
    </>
  );
}
